<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'next/button/Button.vue';

const props = defineProps({
  plan: {
    type: Object,
    required: true,
  },
  billingCycle: {
    type: String,
    default: 'monthly',
  },
  isCurrentPlan: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  canUpgrade: {
    type: Boolean,
    default: false,
  },
  canDowngrade: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'upgrade', 'downgrade']);

const { t } = useI18n();

const price = computed(() => {
  if (props.plan.monthly_price === 0) {
    return t('BILLING.PRICING.FREE');
  }
  const value =
    props.billingCycle === 'yearly'
      ? props.plan.yearly_price / 12
      : props.plan.monthly_price;
  // Format without decimals if whole number
  const formatted = value % 1 === 0 ? value.toFixed(0) : value.toFixed(2);
  return formatted.replace('.', ',');
});

// Venda consultiva: mostra "Consulte" no lugar do preço dos planos pagos.
// Pra reexibir os valores, troque para `false`. Back-end/cobrança intactos.
const CONSULT_PRICING = true;
const showConsult = computed(
  () => CONSULT_PRICING && props.plan.monthly_price > 0
);
const priceLabel = computed(() =>
  showConsult.value ? 'Consulte' : price.value
);

const yearlyTotal = computed(() => {
  if (props.plan.yearly_price === 0) return null;
  const formatted =
    props.plan.yearly_price % 1 === 0
      ? props.plan.yearly_price.toFixed(0)
      : props.plan.yearly_price.toFixed(2);
  return formatted.replace('.', ',');
});

const discountPercentage = computed(() => {
  return props.plan.yearly_discount_percentage || 0;
});

const badgeColorClass = computed(() => {
  const colors = {
    green: 'bg-n-teal-3 text-n-teal-11 border-n-teal-6',
    blue: 'bg-n-blue-3 text-n-blue-11 border-n-blue-6',
    purple: 'bg-n-violet-3 text-n-violet-11 border-n-violet-6',
    orange: 'bg-n-amber-3 text-n-amber-11 border-n-amber-6',
  };
  return colors[props.plan.badge_color] || colors.blue;
});

const cardClass = computed(() => {
  if (props.isCurrentPlan) {
    return 'border-n-blue-7 bg-n-blue-2 ring-2 ring-n-blue-6';
  }
  return 'border-n-weak bg-n-solid-2 hover:border-n-slate-6';
});

const handleAction = () => {
  if (props.isCurrentPlan) return;
  if (props.canUpgrade) {
    emit('upgrade', props.plan);
  } else if (props.canDowngrade) {
    emit('downgrade', props.plan);
  } else {
    emit('select', props.plan);
  }
};

const buttonVariant = computed(() => {
  if (props.isCurrentPlan) return { faded: true, slate: true };
  if (props.canUpgrade) return { solid: true, blue: true };
  if (props.canDowngrade) return { faded: true, amber: true };
  return { solid: true, slate: true };
});

const buttonText = computed(() => {
  if (props.isCurrentPlan) return t('BILLING.PRICING.CURRENT_PLAN');
  if (props.canUpgrade) return t('BILLING.PRICING.UPGRADE');
  if (props.canDowngrade) return t('BILLING.PRICING.DOWNGRADE');
  return t('BILLING.PRICING.SELECT_PLAN');
});

// Format large numbers (1000 → 1k, 25000 → 25k)
const formatNumber = num => {
  if (num >= 1000) {
    return `${(num / 1000).toFixed(num % 1000 === 0 ? 0 : 1)}k`;
  }
  return num.toString();
};

// Format storage (MB to GB if needed)
const formatStorage = mb => {
  if (mb >= 1000) {
    return `${(mb / 1000).toFixed(mb % 1000 === 0 ? 0 : 1)}GB`;
  }
  return `${mb}MB`;
};

const limits = computed(() => {
  return [
    {
      key: 'agents',
      label: t('BILLING.FEATURES.AGENTS', { count: props.plan.agent_limit }),
    },
    {
      key: 'inboxes',
      label: t('BILLING.FEATURES.INBOXES', { count: props.plan.inbox_limit }),
    },
    {
      key: 'contacts',
      label: t('BILLING.FEATURES.CONTACTS', {
        count: formatNumber(props.plan.contacts_limit),
      }),
    },
    {
      key: 'messages',
      label: t('BILLING.FEATURES.MESSAGES', {
        count: formatNumber(props.plan.messages_per_month),
      }),
    },
    {
      key: 'storage',
      label: t('BILLING.FEATURES.STORAGE', {
        count: formatStorage(props.plan.storage_mb),
      }),
    },
    {
      key: 'automations',
      label: t('BILLING.FEATURES.FLOWS', {
        count: props.plan.automations_limit,
      }),
    },
    {
      key: 'campaigns',
      label: t('BILLING.FEATURES.CAMPAIGNS_LIMIT', {
        count: props.plan.campaigns_limit,
      }),
    },
    {
      key: 'credits',
      label: t('BILLING.FEATURES.AI_CREDITS', {
        count: formatNumber(props.plan.included_credits),
      }),
    },
  ];
});
</script>

<template>
  <div
    class="relative flex flex-col rounded-xl border p-5 transition-all duration-200"
    :class="cardClass"
  >
    <!-- Badge -->
    <div
      v-if="plan.badge_text"
      class="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 text-xs font-semibold rounded-full border"
      :class="badgeColorClass"
    >
      {{ plan.badge_text }}
    </div>

    <!-- Current Plan Indicator -->
    <div
      v-if="isCurrentPlan"
      class="absolute -top-3 right-4 px-3 py-1 text-xs font-semibold rounded-full bg-n-blue-9 text-white"
    >
      {{ t('BILLING.PRICING.YOUR_PLAN') }}
    </div>

    <!-- Plan Header -->
    <div class="text-center mb-4">
      <h3 class="text-lg font-semibold text-n-slate-12">
        {{ plan.name }}
      </h3>
      <p class="text-xs text-n-slate-11 mt-1">
        {{ plan.description }}
      </p>
    </div>

    <!-- Price -->
    <div class="text-center mb-4">
      <div class="flex items-baseline justify-center gap-1">
        <span
          v-if="plan.monthly_price > 0 && !showConsult"
          class="text-sm text-n-slate-10 font-medium"
        >
          R$
        </span>
        <span class="text-2xl font-bold text-n-slate-12">{{ priceLabel }}</span>
        <span
          v-if="plan.monthly_price > 0 && !showConsult"
          class="text-xs text-n-slate-10"
        >
          {{ t('BILLING.CYCLE.PER_MONTH') }}
        </span>
      </div>
      <div
        v-if="billingCycle === 'yearly' && yearlyTotal && !showConsult"
        class="mt-1"
      >
        <span class="text-xs text-n-slate-10">
          R$ {{ yearlyTotal }} {{ t('BILLING.CYCLE.BILLED_YEARLY') }}
        </span>
        <span
          v-if="discountPercentage > 0"
          class="ml-1 text-xs font-medium text-n-teal-11 bg-n-teal-3 px-1.5 py-0.5 rounded"
        >
          -{{ discountPercentage }}%
        </span>
      </div>
    </div>

    <!-- Limits List -->
    <div class="flex-1 space-y-2 mb-4">
      <div
        v-for="limit in limits"
        :key="limit.key"
        class="flex items-center gap-2 text-xs"
      >
        <i class="i-lucide-check text-n-teal-10 shrink-0" />
        <span class="text-n-slate-12">{{ limit.label }}</span>
      </div>
    </div>

    <!-- Action Button -->
    <Button
      class="w-full mt-auto"
      v-bind="buttonVariant"
      :disabled="isCurrentPlan || isLoading"
      :is-loading="isLoading"
      @click="handleAction"
    >
      {{ buttonText }}
    </Button>
  </div>
</template>
